// src/components/SubscriptionFlow/SubscriptionFlow.tsx
// Full subscription checkout flow matching Adobe-style UX:
// Step 1: Plan selection  →  Step 2: Email  →  Step 3: Payment  →  Step 4: Upsell

import React, { useState } from "react";
import {
  CheckmarkCircle20Filled,
  Dismiss20Regular,
  LockClosed16Regular,
  ChevronDown20Regular,
  Phone20Regular,
  Question20Regular,
} from "@fluentui/react-icons";
import type { Plan, Price, Product, BillingCycle } from "../../../../types/billingTypes";
import { Button } from "@fluentui/react-components";

interface SubscriptionFlowProps {
  product:    Product;
  plan:       Plan;                            // the plan the user clicked
  price:      Price;                           // the price for that plan
  allPlans?:  Plan[];                          // all plans for this product (for cycle switching)
  priceMap?:  Record<string, Price>;           // plan_id → Price
  onClose:    () => void;
  onSuccess?: (plan: Plan, price: Price, email: string) => void;
}

type Step = "plan" | "email" | "payment" | "upsell";

// ── Helpers ────────────────────────────────────────────────────
function formatAmount(cents: number): string {
  const d = cents / 100;
  return `$${d % 1 === 0 ? d.toFixed(0) : d.toFixed(2)}`;
}

function getPeriodLabel(plan: Plan): string {
  if (plan.billingInterval === "lifetime") return "one-time";
  if (plan.billingInterval === "yearly")   return "/yr";
  return "/mo";
}

const PRODUCT_META: Record<string, { icon: string; color: string; gradient: string }> = {
  "Hospitality Software":       { icon: "🏨", color: "#92400E", gradient: "linear-gradient(135deg,#92400E,#D97706)" },
  "CRM Software":               { icon: "🤝", color: "#1E40AF", gradient: "linear-gradient(135deg,#1E40AF,#2563EB)" },
  "Email Services":             { icon: "✉️",  color: "#065F46", gradient: "linear-gradient(135deg,#065F46,#059669)" },
  "Swift Grid":                 { icon: "⚡",  color: "#5B21B6", gradient: "linear-gradient(135deg,#5B21B6,#7C3AED)" },
  "Accounting":                 { icon: "📊",  color: "#0E7490", gradient: "linear-gradient(135deg,#0E7490,#0891B2)" },
  "Chat Channel":               { icon: "💬",  color: "#9D174D", gradient: "linear-gradient(135deg,#9D174D,#DB2777)" },
  "Call App":                   { icon: "📞",  color: "#1E3A5F", gradient: "linear-gradient(135deg,#1E3A5F,#1D4ED8)" },
  "Agentic Customer Services":  { icon: "🤖",  color: "#78350F", gradient: "linear-gradient(135deg,#78350F,#B45309)" },
  "API Access (Pay-As-You-Go)": { icon: "🔌",  color: "#1E40AF", gradient: "linear-gradient(135deg,#1E3A8A,#1E40AF)" },
  "Main SaaS Platform":         { icon: "🏢",  color: "#1E293B", gradient: "linear-gradient(135deg,#1E293B,#334155)" },
  "White Label Add-on":         { icon: "🎨",  color: "#4C1D95", gradient: "linear-gradient(135deg,#4C1D95,#6D28D9)" },
};
const DEFAULT_META = { icon: "📦", color: "#1473e6", gradient: "linear-gradient(135deg,#1473e6,#0d66d0)" };

// ── Cycle-aware price display ──────────────────────────────────
function getDisplayPrice(price: Price, cycle: BillingCycle): { amount: string; period: string; note?: string } {
  if (price.pricing_model === "metered") {
    const u = (price.unit_amount ?? 0) / 100;
    const label = price.unit_label ?? "unit";
    return {
      amount: u === 0 ? "Free" : `$${u < 0.01 ? u.toFixed(4) : u.toFixed(2)}`,
      period: `per ${label}`,
      note:   price.free_units > 0 ? `First ${price.free_units.toLocaleString()} ${label}s free` : undefined,
    };
  }
  if (price.pricing_model === "per_seat") {
    const d = (price.unit_amount ?? 0) / 100;
    return { amount: d === 0 ? "Free" : `$${d % 1 === 0 ? d.toFixed(0) : d.toFixed(2)}`, period: "per seat / mo" };
  }
  if (price.amount === 0) return { amount: "Free", period: "forever" };
  if (cycle === "yearly") {
    const monthly = price.amount / 12 / 100;
    return {
      amount: `$${monthly % 1 === 0 ? monthly.toFixed(0) : monthly.toFixed(2)}`,
      period: "/mo",
      note: `Billed $${(price.amount / 100).toFixed(0)}/year`,
    };
  }
  const d = price.amount / 100;
  return { amount: `$${d % 1 === 0 ? d.toFixed(0) : d.toFixed(2)}`, period: "/mo" };
}

// ── CartPanel ──────────────────────────────────────────────────
function CartPanel({ product, plan, price, meta, cycle, availableCycles, onCycleChange }: {
  product: Product; plan: Plan; price: Price;
  meta: { icon: string; color: string; gradient: string };
  cycle: BillingCycle;
  availableCycles: BillingCycle[];
  onCycleChange: (c: BillingCycle) => void;
}) {
  const { amount, period, note } = getDisplayPrice(price, cycle);
  const hasTrial = plan.trialDays > 0;

  const cycleLabels: Record<BillingCycle, string> = {
    monthly:  "Monthly",
    yearly:   "Annual, billed yearly",
    lifetime: "Lifetime (one-time)",
  };

  return (
    <div style={{
      flexShrink: 0,
      padding: "32px 28px",
      background: "#fff",
      display: "flex", flexDirection: "column", gap: 0,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 20 }}>Your cart</div>

      {/* Billing cycle selector — now a real <select> */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 6 }}>Billing cycle</div>
        <div style={{ position: "relative" }}>
          <select
            value={cycle}
            onChange={e => onCycleChange(e.target.value as BillingCycle)}
            style={{
              width: "100%", height: 40, borderRadius: 8,
              border: "1.5px solid #e5e7eb",
              padding: "0 36px 0 12px",
              fontSize: 13, fontWeight: 600, color: "#0f172a",
              background: "#fff", outline: "none", cursor: "pointer",
              appearance: "none", WebkitAppearance: "none",
            }}
          >
            {availableCycles.map(c => (
              <option key={c} value={c}>{cycleLabels[c]}</option>
            ))}
          </select>
          <ChevronDown20Regular style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            color: "#6b7280", pointerEvents: "none", fontSize: 16,
          }} />
          {cycle === "yearly" && (
            <div style={{
              position: "absolute", right: 34, top: "50%", transform: "translateY(-50%)",
              background: "#dcfce7", color: "#166534",
              fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 6,
              pointerEvents: "none", whiteSpace: "nowrap",
            }}>Save 20%</div>
          )}
        </div>
      </div>

      {/* Product card */}
      <div style={{
        border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 16px",
        display: "flex", alignItems: "center", gap: 12, marginBottom: 20, position: "relative",
      }}>
        {hasTrial && (
          <div style={{
            position: "absolute", top: -10, right: 12,
            background: "#f59e0b", color: "#fff",
            fontSize: 10, fontWeight: 700, padding: "2px 10px", borderRadius: 20, whiteSpace: "nowrap",
          }}>
            {plan.trialDays}-day free trial
          </div>
        )}
        <div style={{
          width: 44, height: 44, borderRadius: 10, background: meta.gradient,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0,
        }}>{meta.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{product.name}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{plan.name}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
            {amount}<span style={{ fontSize: 11, fontWeight: 400 }}>{period}</span>
          </div>
          {note && <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>{note}</div>}
        </div>
      </div>

      {/* Subtotal / Due now */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: "#6b7280" }}>Subtotal</span>
        <span style={{ fontSize: 13, color: "#6b7280" }}>{amount}{period}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: 8, borderTop: "1px solid #f3f4f6" }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Due now</span>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
            {hasTrial ? "$0" : amount}
            <span style={{ fontSize: 12, fontWeight: 400 }}>{hasTrial ? "" : period}</span>
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>+ applicable tax</div>
        </div>
      </div>

      {hasTrial && (
        <div style={{ marginTop: 12, fontSize: 11, color: "#6b7280", background: "#f9fafb", borderRadius: 8, padding: "10px 12px", lineHeight: 1.6 }}>
          After your {plan.trialDays}-day trial, you'll be charged {amount}{period}. Cancel anytime before then.
        </div>
      )}
    </div>
  );
}

// ── Step 1: Plan chooser ──────────────────────────────────────
function StepPlan({ plan, price, meta, cycle, onContinue }: {
  plan: Plan; price: Price;
  meta: { icon: string; color: string; gradient: string };
  cycle: BillingCycle;
  onContinue: () => void;
}) {
  const features = (plan.features?.$values ?? []).filter(f => f.displayLabel);
  const { amount, period, note } = getDisplayPrice(price, cycle);

  return (
    <div style={{ padding: "32px 36px", flex: 1, overflowY: "auto" }}>
      {/* Product header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 14,
          background: meta.gradient,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0,
        }}>{meta.icon}</div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.3px" }}>{plan.productName}</div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>{plan.name} plan</div>
        </div>
      </div>

      {/* Price display */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <span style={{ fontSize: 40, fontWeight: 900, color: "#0f172a", letterSpacing: "-1.5px" }}>{amount}</span>
          <span style={{ fontSize: 15, color: "#6b7280", fontWeight: 500 }}>{period}</span>
        </div>
        {note && (
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>{note}</div>
        )}
        {plan.trialDays > 0 && (
          <div style={{ fontSize: 13, color: "#059669", fontWeight: 600, marginTop: 6 }}>
            Includes {plan.trialDays}-day free trial
          </div>
        )}
      </div>

      {/* Description */}
      {plan.description && (
        <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, margin: "0 0 24px" }}>
          {plan.description}
        </p>
      )}

      {/* Features */}
      {features.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 12 }}>
            What's included
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {features.map(f => (
              <div key={f.featureId} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: "#374151" }}>
                <CheckmarkCircle20Filled style={{ color: meta.color, flexShrink: 0, marginTop: 1 }} />
                <span>{f.displayLabel}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Continue */}
      <Button
        onClick={onContinue}
        style={{
          width: "100%", height: 48, borderRadius: 24,
          background: meta.color, color: "#fff",
          border: "none", fontSize: 15, fontWeight: 700,
          cursor: "pointer", transition: "opacity 0.15s",
          letterSpacing: "0.1px",
          padding: "12px",
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.88"; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
      >
        Continue
      </Button>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 14, fontSize: 12, color: "#9ca3af" }}>
        <LockClosed16Regular />
        Secure transaction
      </div>
    </div>
  );
}

// ── Step 2: Email ──────────────────────────────────────────────
function StepEmail({ meta, onContinue }: {
  meta: { color: string };
  onContinue: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    onContinue(email);
  };

  return (
    <div style={{ padding: "40px 36px", flex: 1 }}>
      {/* Step indicator */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 20 }}>
        Step 1 of 2 — Account
      </div>

      <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: "0 0 8px", letterSpacing: "-0.3px", lineHeight: 1.3 }}>
        Enter your email to get started.
      </h2>
      <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 28px", lineHeight: 1.6 }}>
        We'll create an account if you're new, or sign you in if you already have one.
      </p>

      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
        Email address
      </label>
      <input
        type="email"
        value={email}
        onChange={e => { setEmail(e.target.value); setError(""); }}
        onKeyDown={e => e.key === "Enter" && handleSubmit()}
        placeholder="you@example.com"
        style={{
          width: "100%", height: 44, borderRadius: 8,
          border: error ? "1.5px solid #ef4444" : "1.5px solid #d1d5db",
          padding: "0 14px", fontSize: 14, color: "#0f172a",
          outline: "none", boxSizing: "border-box",
          transition: "border-color 0.15s",
        }}
        onFocus={e => { (e.currentTarget as HTMLInputElement).style.borderColor = meta.color; }}
        onBlur={e => { if (!error) (e.currentTarget as HTMLInputElement).style.borderColor = "#d1d5db"; }}
      />
      {error && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 6 }}>{error}</div>}

      <p style={{ fontSize: 11, color: "#9ca3af", margin: "16px 0 28px", lineHeight: 1.6 }}>
        By clicking "Continue", you agree to our{" "}
        <span style={{ color: meta.color, cursor: "pointer", textDecoration: "underline" }}>Terms of Service</span>{" "}
        and{" "}
        <span style={{ color: meta.color, cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</span>.
      </p>

      <button
        onClick={handleSubmit}
        style={{
          width: "100%", height: 48, borderRadius: 24,
          background: meta.color, color: "#fff",
          border: "none", fontSize: 15, fontWeight: 700,
          cursor: "pointer", transition: "opacity 0.15s",
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.88"; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
      >
        Continue
      </button>
    </div>
  );
}

// ── Step 3: Payment ────────────────────────────────────────────
function StepPayment({ meta, email, plan, price, onContinue }: {
  meta: { color: string }; email: string; plan: Plan; price: Price;
  onContinue: () => void;
}) {
  const [payMethod, setPayMethod] = useState<"card" | "paypal">("card");
  const [form, setForm] = useState({ card: "", expiry: "", cvv: "", first: "", last: "", zip: "", country: "Nigeria" });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const field = (key: keyof typeof form, label: string, placeholder: string, half = false, type = "text") => (
    <div style={{ flex: half ? "0 0 calc(50% - 6px)" : "1 1 100%", display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</label>
      <input
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{
          height: 42, borderRadius: 7,
          border: errors[key] ? "1.5px solid #ef4444" : "1.5px solid #d1d5db",
          padding: "0 12px", fontSize: 13, color: "#0f172a",
          outline: "none", boxSizing: "border-box",
        }}
        onFocus={e => { (e.currentTarget as HTMLInputElement).style.borderColor = meta.color; }}
        onBlur={e => { (e.currentTarget as HTMLInputElement).style.borderColor = errors[key] ? "#ef4444" : "#d1d5db"; }}
      />
      {errors[key] && <div style={{ fontSize: 11, color: "#ef4444" }}>{errors[key]}</div>}
    </div>
  );

  const handleSubmit = () => {
    const e: Record<string, string> = {};
    if (payMethod === "card") {
      if (!form.card)   e.card   = "Card number is required.";
      if (!form.expiry) e.expiry = "Expiry is required.";
      if (!form.first)  e.first  = "Required.";
      if (!form.last)   e.last   = "Required.";
    }
    if (Object.keys(e).length) { setErrors(e); return; }
    onContinue();
  };

  const amount  = formatAmount(price.amount);
  const period  = getPeriodLabel(plan);
  const hasTrial = plan.trialDays > 0;

  return (
    <div style={{ padding: "32px 36px", flex: 1, overflowY: "auto" }}>
      {/* Step indicator */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 20 }}>
        Step 2 of 2 — Payment
      </div>

      {/* Account confirmed row */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#f9fafb", borderRadius: 10, padding: "12px 16px", marginBottom: 24,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CheckmarkCircle20Filled style={{ color: "#059669" }} />
          <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{email}</span>
        </div>
        <ChevronDown20Regular style={{ color: "#9ca3af" }} />
      </div>

      <h2 style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", margin: "0 0 20px", letterSpacing: "-0.2px", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#f1f5f9", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>💳</span>
        Enter payment info to get started
      </h2>

      {/* Payment method selector */}
      <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>

        {/* Card option */}
        <div
          style={{ padding: "14px 18px", cursor: "pointer", background: payMethod === "card" ? "#fafbff" : "#fff" }}
          onClick={() => setPayMethod("card")}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: payMethod === "card" ? 16 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 18, height: 18, borderRadius: "50%",
                border: `2px solid ${payMethod === "card" ? meta.color : "#d1d5db"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {payMethod === "card" && <div style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color }} />}
              </div>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>Credit / Debit</span>
            </div>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {["VISA", "MC", "AMEX", "DISC"].map(c => (
                <div key={c} style={{ fontSize: 9, fontWeight: 800, color: "#475569", background: "#f1f5f9", padding: "2px 5px", borderRadius: 3, border: "1px solid #e2e8f0" }}>{c}</div>
              ))}
            </div>
          </div>

          {payMethod === "card" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {field("card",   "Card number", "•••• •••• •••• ••••")}
              {field("expiry", "MM / YY",     "MM / YY", true)}
              {field("cvv",    "CVV",          "•••",     true)}
              {field("first",  "First name",  "First name", true)}
              {field("last",   "Last name",   "Last name",  true)}
              {field("zip",    "Zip / postal code", "Zip code", true)}
              <div style={{ flex: "0 0 calc(50% - 6px)", display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Country / Region</label>
                <select
                  value={form.country}
                  onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                  style={{ height: 42, borderRadius: 7, border: "1.5px solid #d1d5db", padding: "0 12px", fontSize: 13, color: "#0f172a", background: "#fff", outline: "none", boxSizing: "border-box" }}
                >
                  {["Nigeria", "United States", "United Kingdom", "Canada", "Australia", "Germany", "France", "India", "Singapore"].map(c => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "#f3f4f6" }} />

        {/* PayPal option */}
        <div
          style={{ padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
          onClick={() => setPayMethod("paypal")}
        >
          <div style={{
            width: 18, height: 18, borderRadius: "50%",
            border: `2px solid ${payMethod === "paypal" ? meta.color : "#d1d5db"}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {payMethod === "paypal" && <div style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color }} />}
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#003087" }}>Pay<span style={{ color: "#009cde" }}>Pal</span></span>
        </div>
      </div>

      {/* Legal */}
      <p style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.6, margin: "0 0 20px" }}>
        By clicking "Agree and subscribe", you agree to be charged{" "}
        <strong style={{ color: "#6b7280" }}>{hasTrial ? `$0 today, then ${amount}${period}` : `${amount}${period}`}</strong>{" "}
        until you cancel. You can cancel anytime from your account settings. No hidden fees.
      </p>

      <Button
        onClick={handleSubmit}
        style={{
          width: "100%", height: 48, borderRadius: 24,
          background: meta.color, color: "#fff",
          border: "none", fontSize: 15, fontWeight: 700,
          cursor: "pointer", transition: "opacity 0.15s",
          letterSpacing: "0.1px",
          padding: "12px",
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.88"; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
      >
        Agree and subscribe
      </Button>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 14, fontSize: 12, color: "#9ca3af" }}>
        <LockClosed16Regular />
        Secure transaction
      </div>
    </div>
  );
}

// ── Step 4: Upsell / success ───────────────────────────────────
function StepUpsell({ meta, plan, onClose, onSuccess, plan: _plan, price, email }: {
  meta: { color: string; icon: string }; plan: Plan; price: Price; email: string;
  onClose: () => void; onSuccess?: () => void;
}) {
  return (
    <div style={{ padding: "40px 36px", flex: 1 }}>
      {/* Success banner */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginBottom: 32 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{meta.icon}</div>
        <CheckmarkCircle20Filled style={{ color: "#059669", fontSize: 32, marginBottom: 8 }} />
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: "0 0 8px" }}>You're all set!</h2>
        <p style={{ fontSize: 13, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
          Your <strong>{plan.name}</strong> subscription is active. A confirmation has been sent to <strong>{email}</strong>.
        </p>
      </div>

      {/* Additional offer */}
      <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>
        Additional offers, curated just for you.
      </div>

      <div style={{
        border: "1.5px solid #e5e7eb", borderRadius: 12,
        padding: "20px",  marginBottom: 24,
        display: "flex", alignItems: "flex-start", gap: 16,
      }}>
        <div style={{ width: 40, height: 40, borderRadius: 8, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
          🛡️
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>Priority Support Add-on</div>
          <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5, marginBottom: 12 }}>
            Get dedicated support with guaranteed 2-hour response times, a dedicated account manager, and monthly check-in calls.
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <button style={{
              padding: "7px 18px", borderRadius: 20, border: `1.5px solid ${meta.color}`,
              background: "transparent", color: meta.color,
              fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
              Add — $9/mo
            </button>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>$9<span style={{ fontSize: 11, fontWeight: 400 }}>/mo</span></span>
          </div>
        </div>
      </div>

      {/* Continue to dashboard */}
      <button
        onClick={() => { onSuccess?.(); onClose(); }}
        style={{
          width: "100%", height: 48, borderRadius: 24,
          background: meta.color, color: "#fff",
          border: "none", fontSize: 15, fontWeight: 700,
          cursor: "pointer", transition: "opacity 0.15s",
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.88"; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
      >
        Go to your dashboard
      </button>
      <button
        onClick={onClose}
        style={{
          width: "100%", height: 40, borderRadius: 24, border: "none",
          background: "transparent", color: "#9ca3af",
          fontSize: 13, cursor: "pointer", marginTop: 8,
        }}
      >
        Skip for now
      </button>
    </div>
  );
}

// ── Main SubscriptionFlow modal ────────────────────────────────
const SubscriptionFlow: React.FC<SubscriptionFlowProps> = ({
  product, plan, price, allPlans = [], priceMap = {}, onClose, onSuccess,
}) => {
  const [step, setStep]   = useState<Step>("plan");
  const [email, setEmail] = useState("");
  const [cycle, setCycle] = useState<BillingCycle>(plan.billingInterval as BillingCycle ?? "monthly");

  // Guard after hooks
  const meta = PRODUCT_META[product?.name ?? ""] ?? DEFAULT_META;
  if (!product || !plan || !price) return null;

  // Derive available cycles from allPlans for this product
  const cycleOrder: BillingCycle[] = ["monthly", "yearly", "lifetime"];
  const availableCycles: BillingCycle[] = allPlans.length > 0
    ? cycleOrder.filter(c => allPlans.some(p => p.billingInterval === c))
    : [plan.billingInterval as BillingCycle];

  // Find the best matching plan for the selected cycle
  const activePlan: Plan = allPlans.length > 0
    ? (allPlans.find(p => p.billingInterval === cycle && p.sortOrder === plan.sortOrder)
      ?? allPlans.find(p => p.billingInterval === cycle)
      ?? plan)
    : plan;

  // Find the price for the active plan
  const activePrice: Price = priceMap[activePlan.id] ?? price;

  const stepTitles: Record<Step, string> = {
    plan:    "Choose a subscription",
    email:   "Create your account",
    payment: "Enter payment info",
    upsell:  "You're subscribed!",
  };

  return (
    <>
      <style>{`
        .sf-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(0,0,0,0.6);
          display: flex; align-items: center; justify-content: center;
          padding: 24px;
          animation: sfFadeIn 0.18s ease;
        }
        @keyframes sfFadeIn { from { opacity: 0 } to { opacity: 1 } }

        .sf-modal {
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: 1020px;
          height: 88vh;
          max-height: 820px;
          box-shadow: 0 32px 100px rgba(0,0,0,0.28);
          animation: sfSlideUp 0.2s ease;
        }
        @keyframes sfSlideUp { from { transform: translateY(16px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }

        .sf-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 28px;
          border-bottom: 1px solid #f3f4f6;
          flex-shrink: 0;
        }
        .sf-body {
          display: flex;
          flex: 1;
          overflow: hidden;
          min-height: 0;
        }
        .sf-left {
          flex: 1;
          overflow-y: auto;
          min-width: 0;
        }
        .sf-close-btn {
          width: 32px; height: 32px; border-radius: 50%;
          border: none; background: #f3f4f6; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #6b7280; transition: background 0.15s;
        }
        .sf-close-btn:hover { background: #e5e7eb; }

        .sf-cart-panel {
          width: 380px;
          flex-shrink: 0;
          overflow-y: auto;
          border-left: 1px solid #f3f4f6;
        }

        @media (max-width: 768px) {
          .sf-overlay { padding: 0; align-items: flex-end; }
          .sf-modal { max-width: 100%; border-radius: 16px 16px 0 0; height: 95vh; max-height: 95vh; }
          .sf-cart-panel { display: none; }
        }
      `}</style>

      <div className="sf-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="sf-modal">

          {/* Header */}
          <div className="sf-header">
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {/* Step dots */}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {(["plan", "email", "payment", "upsell"] as Step[]).map((s, i) => (
                  <div key={s} style={{
                    width: step === s ? 20 : 8, height: 8, borderRadius: 4,
                    background: step === s ? meta.color
                      : (["plan", "email", "payment", "upsell"].indexOf(step) > i ? "#10b981" : "#e5e7eb"),
                    transition: "all 0.25s ease",
                  }} />
                ))}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>{stepTitles[step]}</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#6b7280" }}>
                <Phone20Regular style={{ fontSize: 16 }} />
                <span>Support</span>
              </div>
              <button className="sf-close-btn" onClick={onClose} aria-label="Close">
                <Dismiss20Regular />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="sf-body">
            <div className="sf-left">
              {step === "plan" && (
                <StepPlan
                  plan={activePlan} price={activePrice} meta={meta} cycle={cycle}
                  onContinue={() => setStep("email")}
                />
              )}
              {step === "email" && (
                <StepEmail
                  meta={meta}
                  onContinue={em => { setEmail(em); setStep("payment"); }}
                />
              )}
              {step === "payment" && (
                <StepPayment
                  meta={meta} email={email} plan={activePlan} price={activePrice}
                  onContinue={() => setStep("upsell")}
                />
              )}
              {step === "upsell" && (
                <StepUpsell
                  meta={meta} plan={activePlan} price={activePrice} email={email}
                  onClose={onClose}
                  onSuccess={() => onSuccess?.(activePlan, activePrice, email)}
                />
              )}
            </div>

            {/* Right: cart summary — hidden on upsell step */}
            {step !== "upsell" && (
              <div className="sf-cart-panel">
                <CartPanel
                  product={product}
                  plan={activePlan}
                  price={activePrice}
                  meta={meta}
                  cycle={cycle}
                  availableCycles={availableCycles}
                  onCycleChange={setCycle}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionFlow;